import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DoorHangerIcon, MapPin3Icon } from "@/shared/icons";
import { Button, Modal, TabsNavigation } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import useGetHotelElevator from "@/domains/hotel/hooks/useGetHotelElevator";
import HotelRoomCheckbox from "@/domains/hotel/components/HotelRoomCheckbox";

type Props = {
  hotelId: number | null;
};

export default function HotelRoomSelectContainer({ hotelId }: Props) {
  const { t } = useTranslation("hotel");
  const tabsList = useMemo(() => ["1", "2", "3", "4", "5"], []);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState<number | null>(null);

  const { myRoom, data, isElevatorLoading, onClose, isEntering, enterInRoom } =
    useGetHotelElevator(hotelId);

  const rooms = useMemo(() => {
    const roomsList = data?.rooms;
    if (roomsList) {
      return roomsList.map((item) => ({
        ...item,
        checked: item.estateId === selectedRoom,
      }));
    }
    return [];
  }, [data?.rooms, selectedRoom]);

  const handleRoomSelect = useCallback((estateId: number) => {
    setSelectedRoom((prev) => (prev === estateId ? null : estateId));
  }, []);

  const changeSelectedTab = useCallback(
    (tab: string) => {
      const index = tabsList.findIndex((item) => item === tab);
      if (index >= 0) {
        setSelectedTabIndex(index);
      }
    },
    [tabsList],
  );

  const enterInMyRoom = useCallback(() => {
    if (myRoom?.id) {
      enterInRoom(myRoom?.id);
    }
  }, [enterInRoom, myRoom?.id]);

  const enterInHotelRoom = useCallback(() => {
    if (selectedRoom) {
      enterInRoom(selectedRoom);
    }
  }, [enterInRoom, selectedRoom]);

  const title = !isElevatorLoading ? (
    <div className="flex gap-3 items-center">
      <MapPin3Icon className="w-5.5" />
      <p className="text-20 font-bold leading-none">{data?.name}</p>
    </div>
  ) : null;

  const footer = (
    <div className="w-full flex gap-2" data-mz-accent="green">
      {myRoom?.room && (
        <Button
          className="grow gap-2 px-4 h-11"
          onClick={enterInMyRoom}
          loading={isEntering}
          disabled={isEntering}
        >
          <DoorHangerIcon className="w-5" />
          {t("hotel.enterRoom")}
          <span className="ml-auto text-13 font-bold">#{myRoom.room}</span>
        </Button>
      )}
      <Button
        className="gap-2 h-11 px-6 bg-gradient-to-r from-[#1C1C1C] to-dark-800 hover:brightness-110"
        variant="soft"
        onClick={enterInHotelRoom}
        loading={isEntering}
        disabled={isEntering || !selectedRoom}
      >
        <DoorHangerIcon className="w-5" />
        {t("hotel.enter")}
      </Button>
    </div>
  );

  const columnsCount = 5;
  const itemsPerColumn = 20;
  const columns = Array.from({ length: columnsCount }, (_, colIndex) => {
    const start = colIndex * itemsPerColumn;
    const end = start + itemsPerColumn;
    return rooms?.slice(start, end) || [];
  });

  return (
    <Modal
      open
      title={title}
      footer={footer}
      onClose={onClose}
      className="grow-0 w-[33rem] h-[49.125rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      style={{ fontFamily: "TT Norms Pro" }}
    >
      {isElevatorLoading || isEntering ? (
        <Loader />
      ) : (
        <>
          <TabsNavigation
            tabs={tabsList}
            selectedTab={tabsList[selectedTabIndex]}
            i18fileName="hotel"
            i18Path="hotel.floor"
            onClick={changeSelectedTab}
            tabClassName="grow"
          />
          <div className="flex items-center gap-1.5 mt-5 mb-3.5">
            <DoorHangerIcon className="size-5 text-white/50" />
            <p className="text-11 font-bold text-white/70 uppercase leading-none">
              {t("hotel.roomsOnLevel")}
            </p>
          </div>
          <div className="grid grid-cols-5 gap-4.5">
            {columns.map((column) => (
              <div key={column[0]?.estateId || Math.random()} className="flex flex-col gap-2">
                {column
                  .filter((item) => item?.floor === selectedTabIndex + 1)
                  .map((item) => (
                    <HotelRoomCheckbox
                      key={item?.estateId}
                      id={item?.estateId}
                      checked={item?.checked || false}
                      onChange={() => handleRoomSelect(item.estateId)}
                      text={item?.room?.toString()}
                      disabled={item?.isLocked && myRoom?.room !== item?.room}
                      hasOwner={item?.hasOwner}
                      isMyRoom={myRoom?.room === item?.room}
                    />
                  ))}
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
